<script setup>
import axios from "axios";
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Pin, ChevronLeft, ChevronRight } from "lucide-vue-next";

const router = useRouter();
const route = useRoute();

const props =defineProps({
    totalPinnedPost: {
        type: Number,
    }
})
const pinnedPosts = ref([]);
const loading = ref(false);
const currentIndex = ref(0);
let interval = null;

// Fetch pinned posts from API
const fetchPinnedPosts = async () => {
    loading.value = true;
    try {
        const channelHandle = route.params.handle;

        const filters = {
            pins: true,
            types: [],
            channel: channelHandle,
        };

        const response = await axios.get("/api/posts", {
            params: {
                filters: filters,
                per_page: 4
            },
        });

        const data = response.data.data;
        pinnedPosts.value = data;
    } catch (error) {
        console.error("Failed to fetch pinned posts:", error);
        pinnedPosts.value = [];
    } finally {
        loading.value = false;
    }
};

const goToSlide = (index) => {
    currentIndex.value = index;
};

const nextSlide = () => {
    if (pinnedPosts.value.length > 0) {
        currentIndex.value =
            (currentIndex.value + 1) % pinnedPosts.value.length;
    }
};

const prevSlide = () => {
    if (pinnedPosts.value.length > 0) {
        currentIndex.value =
            (currentIndex.value - 1 + pinnedPosts.value.length) %
            pinnedPosts.value.length;
    }
};

const openFilters = () => {
    router.push({
        name: "channels.show.filter",
        params: {
            handle: route.params.handle,
        },
        query: {
            filters: btoa(JSON.stringify({ pins: true, types: [] })),
        },
    });
};

// Watch for channel parameter changes to reload pinned posts
watch(
    () => route.params.handle,
    () => {
        fetchPinnedPosts();
    }
);

onMounted(() => {
    fetchPinnedPosts();

    // Auto slide every 5 seconds
    interval = setInterval(() => {
        if (pinnedPosts.value.length > 1) {
            nextSlide();
        }
    }, 5000);
});

onUnmounted(() => {
    if (interval) {
        clearInterval(interval);
    }
});
</script>
<template>
    <div
        v-if="pinnedPosts.length > 0"
        class="sticky top-0 z-30 border-t border-primary/20 bg-black-foreground"
    >
        <div class="flex items-start p-2">
            <!-- Left indicators -->
            <div class="flex flex-col items-center mr-2 space-y-1">
                <div
                    v-for="(post, index) in pinnedPosts"
                    :key="post.id"
                    @click="goToSlide(index)"
                    class="w-2 h-2 rounded-full cursor-pointer transition-all duration-300"
                    :class="
                        index === currentIndex ? 'bg-primary' : 'bg-primary/30'
                    "
                ></div>
            </div>

            <!-- Carousel content -->
            <div class="relative flex-1 h-[50px] overflow-hidden">
                <div class="flex items-start h-full">
                    <div class="w-full px-6" @click="openFilters">
                        <transition-group
                            name="slide"
                            tag="div"
                            class="relative w-full h-full"
                        >
                            <div
                                v-for="(post, index) in pinnedPosts"
                                :key="post.id"
                                v-show="index === currentIndex"
                                class="absolute w-full transition-opacity duration-300 cursor-pointer"
                            >
                                <div v-if="post.files && post.files.length < 1">
                                    <p class="text-sm text-info">
                                        Pinned Post #{{ index + 1 }}
                                    </p>
                                    <p
                                        v-if="post.title"
                                        class="line-clamp-1 text-muted text-xs"
                                    >
                                        {{ post.title }}
                                    </p>
                                    <p
                                        class="line-clamp-1 text-muted text-xs"
                                    >
                                        {{ post.content }}
                                    </p>
                                </div>
                                <div
                                    v-else-if="
                                        post.files && post.files.length > 0
                                    "
                                    class="flex gap-x-1 items-start"
                                >
                                    <div
                                        v-if="post.files[0].type != 3"
                                        class="w-21 h-11 rounded overflow-hidden"
                                    >
                                        <img
                                            v-if="post.files.length > 0"
                                            :src="
                                                post.files[0].type == 1
                                                    ? post.files[0].url
                                                    : post.files[0].type == 2
                                                    ? post.files[0].poster.url
                                                    : ''
                                            "
                                            alt="Post image"
                                            class="w-full h-full rounded object-cover"
                                        />
                                    </div>
                                    <div>
                                        <p class="text-sm text-info">
                                            Pinned Post #{{ index + 1 }}
                                        </p>
                                        <p
                                            v-if="post.title"
                                            class="line-clamp-1 text-muted text-xs"
                                        >
                                            {{ post.title }}
                                        </p>
                                        <p
                                            v-if="post.content"
                                            class="line-clamp-1 text-muted text-xs"
                                        >
                                            {{ post.content }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </transition-group>
                    </div>
                </div>
            </div>

            <!-- Pin icon with count -->
            <div class="flex flex-col items-center gap-y-1">
                <Pin class="size-5 rotate-[45deg] ml-2" />
                <span class="text-xs text-primary-foreground/50">{{
                    totalPinnedPost
                }}</span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: opacity 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
}
</style>

